// src/components/NeuralNetworkModule/AddConstructions.jsx
import React, { useState, useEffect } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CTable,
  CTableHead,
  CTableBody,
  CTableHeaderCell,
  CTableRow,
  CTableDataCell,
  CSpinner,
  CForm,
  CFormInput,
  CFormLabel,
  CFormCheck,
  CInputGroup,
  CInputGroupText,
} from "@coreui/react";
import axios from "axios";

const AddConstructions = ({ visible, onClose }) => {
  const [constructions, setConstructions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    ClassID: "",
    ClassName: "",
    NameOut: "",
    Note: "",
    Threshold: 0,
  });
  const [isEditing, setIsEditing] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (visible) {
      loadConstructions();
    } else {
      setConstructions([]);
      setSelectedRow(null);
    }
  }, [visible]);

  const loadConstructions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/neural_network_module/firstlevelclasses/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (Array.isArray(response.data)) {
        setConstructions(response.data);
      } else {
        setConstructions([]);
      }
    } catch (error) {
      alert(
        "Ошибка при загрузке конструкций: " +
          (error.response?.data?.message || error.message),
      );
      setConstructions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (item) => {
    setSelectedRow(item);
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setFormData({
      ClassID: "",
      ClassName: "",
      NameOut: "",
      Note: "",
      Threshold: 0,
    });
    setShowAddModal(true);
  };

  const handleEditClick = () => {
    if (!selectedRow) {
      alert("Пожалуйста, выберите запись для редактирования");
      return;
    }
    setIsEditing(true);
    setFormData({
      ClassID: selectedRow.ClassID,
      ClassName: selectedRow.ClassName,
      NameOut: selectedRow.NameOut,
      Note: selectedRow.Note,
      Threshold: selectedRow.Threshold,
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = () => {
    if (!selectedRow) {
      alert("Пожалуйста, выберите запись для удаления");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.ClassID ||
      !formData.ClassName ||
      !formData.NameOut ||
      !formData.Note
    ) {
      alert("Пожалуйста, заполните все обязательные поля");
      return;
    }

  try {
      const dataToSend = {
        ID: isEditing ? selectedRow.ID : 0,
        NetworkID: isEditing ? selectedRow.NetworkID : 0, // Передаем 0 для новых записей
        ClassID: formData.ClassID,
        ClassName: formData.ClassName,
        Threshold: isEditing ? formData.Threshold : 0,
        OtherName: "",
        IsManual: true,
        Note: formData.Note,
        NameOut: formData.NameOut,
      };

      if (isEditing && selectedRow) {
        // Редактирование - исправленный URL
        await axios.put(
          `/api/neural_network_module/firstlevelclasses/`,
          dataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      } else {
        // Добавление
        await axios.post(
          `/api/neural_network_module/firstlevelclasses/`,
          dataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      setShowAddModal(false);
      setShowEditModal(false);
      loadConstructions();
      setSelectedRow(null);
    } catch (error) {
      console.error(
        "Ошибка при сохранении:",
        error.response?.data || error.message,
      );
      alert(
        "Ошибка при сохранении: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `/api/neural_network_module/firstlevelclasses/?id=${selectedRow.ID}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setShowDeleteConfirm(false);
      loadConstructions();
      setSelectedRow(null);
    } catch (error) {
      alert(
        "Ошибка при удалении: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  // Стили для ограничения размера модального окна (добавление)
  const modalStyle = {
    height: "auto",
    maxWight: "40vw",
    display: "flex",
    flexDirection: "column",
  };

  // Стили для ограничения размера модального окна (удаление)
  const delmodalStyle = {
    // maxHeight: "20vh",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <>
      <CModal
        alignment="start"
        size="xl"
        visible={visible}
        onClose={onClose}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>Конструкции</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {/* Кнопки управления */}
          <div className="mb-3">
            <CButton
              color="primary"
              size="sm"
              className="me-2"
              onClick={handleAddClick}
            >
              Добавить
            </CButton>
            <CButton
              color="secondary"
              size="sm"
              className="me-2"
              onClick={handleEditClick}
            >
              Редактировать
            </CButton>
            <CButton color="danger" size="sm" onClick={handleDeleteClick}>
              Удалить
            </CButton>
          </div>

          {/* Таблица конструкций */}
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {loading ? (
              <div className="text-center p-3">
                <CSpinner size="sm" />
                <div>Загрузка данных...</div>
              </div>
            ) : (
              <CTable striped bordered hover responsive size="sm">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Класс</CTableHeaderCell>
                    <CTableHeaderCell>Метка</CTableHeaderCell>
                    <CTableHeaderCell>Коэффициент</CTableHeaderCell>
                    <CTableHeaderCell>Выводимое значение</CTableHeaderCell>
                    <CTableHeaderCell>Имя нейросети</CTableHeaderCell>
                    <CTableHeaderCell>Группа</CTableHeaderCell>
                    <CTableHeaderCell>Вручную</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {constructions.length > 0 ? (
                    constructions.map((item) => (
                      <CTableRow
                        key={item.ID}
                        onClick={() => handleRowClick(item)}
                        className={
                          selectedRow && selectedRow.ID === item.ID
                            ? "table-success"
                            : ""
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <CTableDataCell>{item.ClassID}</CTableDataCell>
                        <CTableDataCell>{item.ClassName}</CTableDataCell>
                        <CTableDataCell>{item.Threshold}</CTableDataCell>
                        <CTableDataCell>{item.NameOut}</CTableDataCell>
                        <CTableDataCell>Конструкции</CTableDataCell>
                        <CTableDataCell>{item.Note}</CTableDataCell>
                        <CTableDataCell>
                          <CFormCheck
                            type="checkbox"
                            checked={item.IsManual}
                            onChange={() => {}} // Только для отображения
                            disabled
                          />
                        </CTableDataCell>
                      </CTableRow>
                    ))
                  ) : (
                    <CTableRow>
                      <CTableDataCell colSpan="8" className="text-center">
                        Нет данных
                      </CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
            )}
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={onClose}>
            Закрыть
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Модальное окно добавления/редактирования */}
      <CModal
        className="modal-add-construction"
        alignment="center"
        visible={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
        }}
        backdrop="static"
        style={modalStyle}
      >
        <CModalHeader>
          <CModalTitle>
            {isEditing ? "Редактировать класс" : "Добавить новый класс"}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CForm onSubmit={handleSubmit}>
            <div className="mb-3">
              <CFormLabel>
                Класс нейросети <span className="text-danger">*</span>
              </CFormLabel>
              <CInputGroup>
                <CFormInput
                  type="text"
                  name="ClassID"
                  value={formData.ClassID}
                  onChange={handleFormChange}
                  required
                  placeholder="Введите класс"
                />
                <CInputGroupText className="text-danger">*</CInputGroupText>
              </CInputGroup>
            </div>

            <div className="mb-3">
              <CFormLabel>Метка</CFormLabel>
              <CFormInput
                type="text"
                name="ClassName"
                value={formData.ClassName}
                onChange={handleFormChange}
                placeholder="Введите метку"
              />
            </div>

            <div className="mb-3">
              <CFormLabel>Выводимое значение</CFormLabel>
              <CFormInput
                type="text"
                name="NameOut"
                value={formData.NameOut}
                onChange={handleFormChange}
                placeholder="Введите выводимое значение"
              />
            </div>

            <div className="mb-3">
              <CFormLabel>Группа</CFormLabel>
              <CFormInput
                type="text"
                name="Note"
                value={formData.Note}
                onChange={handleFormChange}
                placeholder="Введите группу"
              />
            </div>

            <div className="mb-3">
              <CFormLabel>Чувствительность нейронной сети</CFormLabel>
              <CFormInput
                type="number"
                name="Threshold"
                value={formData.Threshold}
                onChange={handleFormChange}
                disabled={!isEditing}
                step="0.01"
                min="0"
                max="1"
              />
            </div>
          </CForm>
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => {
              setShowAddModal(false);
              setShowEditModal(false);
            }}
          >
            Отмена
          </CButton>
          <CButton color="primary" onClick={handleSubmit}>
            {isEditing ? "Сохранить" : "Добавить"}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Модальное окно подтверждения удаления */}
      <CModal
        className="modal-add-construction__delete"
        alignment="start"
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        backdrop="static"
        style={delmodalStyle}
        size="m"
      >
        <CModalHeader>
          <CModalTitle>Подтверждение удаления</CModalTitle>
        </CModalHeader>
        <CModalBody>
          Вы уверены, что хотите удалить запись "{selectedRow?.ClassName}"?
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Отмена
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>
            Удалить
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default AddConstructions;
